import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect, useContext } from 'react';
import { Router, Route as ReactRouterDOMRoute, useLocation } from 'react-router-dom';
import { useAnalytics } from 'use-analytics';

import FooterContainer from 'components/footer/FooterContainer';
import Banner from 'components/signUpFunnel/Banner';
import SkipContainer from 'components/signUpFunnel/InternalSkip/SkipContainer';
import BillingContainer from 'components/signUpFunnel/pickMeals/BillingContainer';
import MenuContainer from 'components/signUpFunnel/pickMeals/menu/MenuContainer';
import PickYourMealsSlideOut from 'components/signUpFunnel/pickMeals/PickYourMealsSlideOut';
import PickPlanContainer from 'components/signUpFunnel/pickPlan/PickPlanContainer';
import { HOME_CHEF } from 'context/BrandContext';
import * as Routes from 'helpers/routes';
import useFeatureFlag from 'hooks/useFeatureFlag';
import useFunnelMealPlan from 'hooks/useFunnelMealPlan';
import useScrollToTopOnRouteChange from 'hooks/useScrollToTopOnRouteChange';
import { FAMILY, TEMPO, POPULATE_DATA_LAYER, ENABLE_INTERNAL_SKIP_FUNNEL } from 'lib/constants';
import { getBrowserHistory } from 'utilities/router';

import BackgroundContainer from './BackgroundContainer';
import BoxContainer from './box/BoxContainer';
import BoxRecommenderContainer from './boxRecommender/BoxRecommenderContainer';
import CheckoutContainer from './checkout/CheckoutContainer';
import {
  ProgressBarContextProvider,
  UserContextProvider,
  BasketContextProvider,
  VoucherContextProvider,
  PickYourMealsContextProvider,
  PickYourMealsContext,
} from './Contexts';
import FunnelUserWrapper from './FunnelUserWrapper';
import IntroContainer from './intro/IntroContainer';
import MobileProgressBar from './MobileProgressBar';
import NavBar from './pickMeals/NavBar';
import PreferencesAvoidContainer from './preferences/PreferencesAvoidContainer';
import PreferencesDietaryContainer from './preferences/PreferencesDietaryContainer';
import ProgressBar from './ProgressBar';
import PromoContainer from './promo/PromoContainer';
import ThankYouContainer from './thankYou/ThankYouContainer';

const Route = Sentry.withSentryRouting(ReactRouterDOMRoute);
const history = getBrowserHistory();
Sentry.addIntegration(Sentry.reactRouterV5BrowserTracingIntegration({ history }));

const queryClient = new QueryClient();

const updateMainContentStyles = () => {
  const mainContent = document.getElementById('mainContent');
  const siteWrapper = document.getElementsByClassName('siteWrapper')[0];

  if (mainContent) {
    mainContent.style.maxWidth = 'none';
    mainContent.style.height = '100%';
    siteWrapper.style.height = '1px';
  }
};

const App = () => {
  const analytics = useAnalytics();
  const mealPlanSlug = useFunnelMealPlan();

  useScrollToTopOnRouteChange();

  useEffect(() => {
    updateMainContentStyles();
  }, []);

  useEffect(() => {
    analytics.track(POPULATE_DATA_LAYER, { mealPlanSlug });
  }, [analytics, mealPlanSlug]);

  const isInternal = useFeatureFlag(ENABLE_INTERNAL_SKIP_FUNNEL);
  const location = useLocation();
  const isFamilyMealPlan = mealPlanSlug === FAMILY;

  const homeChefRoutes = Routes.getBrandRoute({
    brand: HOME_CHEF,
    isFamily: isFamilyMealPlan,
    addSuffix: false,
  });
  const tempoRoutes = Routes.getBrandRoute({
    brand: TEMPO,
  });

  const { isPickYourMeals, isPickYourMealsSlideOut } = useContext(PickYourMealsContext);

  return (
    <>
      {isPickYourMeals && (
        <FunnelUserWrapper>
          <NavBar />
          <Route exact path={Routes.PICK_MEALS_MENU_PATH}>
            <MenuContainer />
          </Route>
          <Route exact path={tempoRoutes.pickMealsBilling}>
            <BillingContainer />
          </Route>
          <Route exact path={tempoRoutes.pickMealsThankYou}>
            <ThankYouContainer />
          </Route>
        </FunnelUserWrapper>
      )}

      {!isPickYourMeals && !isPickYourMealsSlideOut && (
        <>
          <ProgressBar />
          <MobileProgressBar />
          <FunnelUserWrapper>
            <BackgroundContainer>
              {isFamilyMealPlan && (
                <Route exact path={`${homeChefRoutes.pickPlan}/:mealPlanSlug?`}>
                  <PickPlanContainer />
                </Route>
              )}
              <Route exact path={`${homeChefRoutes.intro}/:mealPlanSlug?`}>
                <IntroContainer />
              </Route>
              <Route exact path={`${homeChefRoutes.preferenceDietary}/:mealPlanSlug?`}>
                <PreferencesDietaryContainer />
              </Route>
              <Route exact path={`${homeChefRoutes.preferenceAvoid}/:mealPlanSlug?`}>
                <PreferencesAvoidContainer />
              </Route>
              <Route exact path={`${homeChefRoutes.promo}/:mealPlanSlug?`}>
                <PromoContainer />
              </Route>
              <Route exact path={`${homeChefRoutes.boxRecommenderPeople}/:mealPlanSlug?`}>
                <BoxRecommenderContainer />
              </Route>
              <Route exact path={`${homeChefRoutes.boxRecommenderMeals}/:mealPlanSlug?`}>
                <BoxRecommenderContainer />
              </Route>
              <Route exact path={`${homeChefRoutes.boxRecommenderDays}/:mealPlanSlug?`}>
                <BoxRecommenderContainer />
              </Route>
              <Route exact path={`${homeChefRoutes.box}/:mealPlanSlug?`}>
                <BoxContainer />
              </Route>
              <Route exact path={`${homeChefRoutes.billing}/:mealPlanSlug?`}>
                <CheckoutContainer />
              </Route>
              <Route exact path={`${homeChefRoutes.thankyou}/:mealPlanSlug?`}>
                <ThankYouContainer />
              </Route>
              {isInternal && !location.pathname.includes(homeChefRoutes.intro) && (
                <SkipContainer mealPlan={mealPlanSlug} />
              )}
            </BackgroundContainer>
          </FunnelUserWrapper>
          <FooterContainer />
        </>
      )}
    </>
  );
};

const FunnelContainer = () => (
  <Router history={history}>
    <VoucherContextProvider>
      <Banner />
      <ProgressBarContextProvider>
        <UserContextProvider>
          <QueryClientProvider client={queryClient}>
            <ReactQueryDevtools initialIsOpen={false} />
            <BasketContextProvider>
              <PickYourMealsContextProvider>
                <App />
                <PickYourMealsSlideOut />
              </PickYourMealsContextProvider>
            </BasketContextProvider>
          </QueryClientProvider>
        </UserContextProvider>
      </ProgressBarContextProvider>
    </VoucherContextProvider>
  </Router>
);

export default FunnelContainer;
